import React, { useState } from "react";
import { Table, Button, Badge, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { fetchConversationTurns } from "../../redux/knowledgeBaseSlice";
import "./QuestionInsights.css";

const QuestionInsights = ({ questions, conversations, onCreateFromQuestion }) => {
  const dispatch = useDispatch();
  const { conversationTurns, conversationTurnsLoading } = useSelector((state) => state.knowledgeBase);
  const [tab, setTab] = useState("gaps");
  const [selectedConversationId, setSelectedConversationId] = useState(null);

  const gaps = questions
    .filter((q) => !q.had_good_match)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const freqMap = {};
  questions.forEach((q) => {
    const key = q.question_text.toLowerCase().trim();
    freqMap[key] = (freqMap[key] || 0) + 1;
  });
  const frequent = Object.entries(freqMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 20);

  const fmt = (ts) => {
    const d = new Date(ts);
    return (
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
      " " +
      d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    );
  };

  const handleSelectConversation = (conversationId) => {
    setSelectedConversationId(conversationId);
    dispatch(fetchConversationTurns(conversationId));
  };

  return (
    <div className="question-insights">
      <div className="mb-3">
        <h5>Question Insights</h5>
        <small className="text-muted">
          {questions.length} question{questions.length !== 1 ? "s" : ""} recorded · {gaps.length} gap
          {gaps.length !== 1 ? "s" : ""} detected
        </small>
      </div>

      {questions.length === 0 ? (
        <div className="text-center py-5 bg-white border rounded">
          <div className="qi-empty-icon">💬</div>
          <p className="text-muted mb-1">No questions recorded yet</p>
          <small className="text-muted">Questions asked in the Ask AI tab will appear here.</small>
        </div>
      ) : (
        <>
          <div className="d-flex gap-2 mb-3">
            {[
              ["gaps", "Coverage Gaps"],
              ["frequent", "Most Asked"],
              ["all", "All Conversations"],
            ].map(([id, label]) => (
              <Button key={id} variant={tab === id ? "dark" : "outline-secondary"} size="sm" onClick={() => setTab(id)}>
                {label}
                {id === "gaps" && gaps.length > 0 && (
                  <Badge bg="danger" className="ms-1">
                    {gaps.length}
                  </Badge>
                )}
              </Button>
            ))}
          </div>

          {tab === "gaps" &&
            (gaps.length === 0 ? (
              <div className="text-center py-4 bg-white border rounded">
                <p className="text-muted mb-0">No gaps detected — the AI answered everything!</p>
              </div>
            ) : (
              <div className="bg-white border rounded overflow-hidden">
                <small className="d-block p-3 text-muted border-bottom">
                  These questions couldn't be answered from the knowledge base. Consider creating articles for these
                  topics.
                </small>
                {gaps.map((q, i) => (
                  <div key={i} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                    <div>
                      <div className="fw-semibold qi-question-title">{q.question_text}</div>
                      <small className="text-muted">{fmt(q.created_at)}</small>
                    </div>
                    <Button
                      size="sm"
                      className="qi-create-article-btn"
                      onClick={() => onCreateFromQuestion(q.question_text)}
                    >
                      + Create Article
                    </Button>
                  </div>
                ))}
              </div>
            ))}

          {tab === "frequent" && (
            <div className="bg-white border rounded overflow-hidden">
              <Table hover responsive className="mb-0" style={{ fontSize: "13px" }}>
                <thead className="bg-light">
                  <tr>
                    <th>Question</th>
                    <th style={{ width: "80px" }}>Asked</th>
                    <th style={{ width: "120px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {frequent.map(([q, count], i) => (
                    <tr key={i}>
                      <td>{q}</td>
                      <td>
                        <Badge bg="secondary">{count}×</Badge>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-warning"
                          className="qi-create-article-btn-frequent"
                          onClick={() => onCreateFromQuestion(q)}
                        >
                          + Create Article
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {tab === "all" && (
            <div className="d-flex qi-split-view">
              {/* Master: conversation list */}
              <div
                className={`bg-white border rounded qi-master-pane ${
                  selectedConversationId ? "qi-master-pane--split" : "qi-master-pane--full"
                }`}
              >
                <div tabIndex={0} role="region" aria-label="Conversation List" className="qi-master-scroll">
                  <Table hover responsive className="mb-0" style={{ fontSize: "13px" }}>
                    <thead className="bg-light qi-master-thead">
                      <tr>
                        <th>Topic</th>
                        <th style={{ width: "70px" }}>Turns</th>
                        {!selectedConversationId && <th style={{ width: "120px" }}>Last Activity</th>}
                        <th style={{ width: "90px" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversations.map((c) => (
                        <tr
                          key={c.conversationId}
                          role="button"
                          onClick={() => handleSelectConversation(c.conversationId)}
                          className={selectedConversationId === c.conversationId ? "qi-conversation-row--selected" : ""}
                        >
                          <td>{c.topic}</td>
                          <td>
                            <small className="text-muted">{c.turnCount}</small>
                          </td>
                          {!selectedConversationId && (
                            <td>
                              <small className="text-muted">{fmt(c.lastActivity)}</small>
                            </td>
                          )}
                          <td>
                            {c.hadAnyMiss === null ? (
                              <Badge bg="secondary">Unknown</Badge>
                            ) : c.hadAnyMiss ? (
                              <Badge bg="danger">Gap</Badge>
                            ) : (
                              <Badge bg="success">Answered</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                      {conversations.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            No conversations found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Detail: reading pane */}
              {selectedConversationId && (
                <div
                  tabIndex={0}
                  role="region"
                  aria-label="Conversation Detail"
                  className="bg-white border rounded p-3 qi-detail-pane"
                >
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Conversation</h6>
                    <Button variant="outline-secondary" size="sm" onClick={() => setSelectedConversationId(null)}>
                      Close
                    </Button>
                  </div>

                  {conversationTurnsLoading && (
                    <div className="text-center py-4">
                      <Spinner animation="border" className="qi-spinner" />
                    </div>
                  )}

                  {!conversationTurnsLoading &&
                    conversationTurns &&
                    conversationTurns.map((turn) => (
                      <React.Fragment key={turn.turnId}>
                        <div className="d-flex justify-content-end mb-2">
                          <div className="qi-bubble-question">{turn.questionText}</div>
                        </div>

                        <div className="d-flex justify-content-start mb-1">
                          <div className="qi-bubble-answer">
                            {turn.answerText || <em className="text-muted">No answer recorded (legacy entry)</em>}
                          </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <small className="text-muted">{fmt(turn.createdAt)}</small>
                          {turn.hadGoodMatch === null ? (
                            <Badge bg="secondary">Unknown</Badge>
                          ) : turn.hadGoodMatch ? (
                            <Badge bg="success">Answered</Badge>
                          ) : (
                            <Badge bg="danger">Gap</Badge>
                          )}
                        </div>
                      </React.Fragment>
                    ))}

                  {!conversationTurnsLoading && conversationTurns && conversationTurns.length === 0 && (
                    <p className="text-muted text-center py-3">No turns found for this conversation.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QuestionInsights;
